<template>
  <div class="px-6 pt-6 pb-0 mx-auto" id="signup" >

    <!-- DIALOGS -->
    <v-dialog v-model="showPasswordDialog" persistent max-width="260">
      <v-card class="pa-3">
        <v-card-text class="body-1 pt-2">{{ $t('registration.rememberPasswordDialog') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="green darken-1" 
            small 
            dark 
            @click="closePasswordDialog"
          >{{ $t('ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <eula-dialog
      v-if="dialogEntitySubmit"
      @close-dialog="dialogEntitySubmit = false"
      showDialog="true"
    />

    <v-container class="py-0" >
      <v-alert 
        v-if="displayAlert == true" 
        class="px-5 mx-auto"
        width="600" 
        :type="showAlert.type"
        :dismissible="!!showAlert.dismissable"
        dense
      >
        {{showAlert.message}}
      </v-alert>

      <v-row class="mx-0 px-0 pb-0">
        <v-col
          :cols="!$vuetify.breakpoint.mobile ? 5 : 12"
          class="mx-0 px-0 pb-0"
          :order="!$vuetify.breakpoint.mobile ? 1 : 12"
        >
          <SignupPanel />
        </v-col>

        <v-col
          :cols="!$vuetify.breakpoint.mobile ? 7 : 12"
          class="mx-0 px-0 pb-0"
          :order="!$vuetify.breakpoint.mobile ? 12 : 1"
        >
          
          <v-card class="mx-0 px-0">
            <v-card-title class="justify-center pa-0">
              <label class="title ">{{ $t('registration.registration') }}</label>
            </v-card-title>
            <v-card-text class="pb-0">
              <v-form ref="signUpForm" v-model="formValidity">
                <v-container class="py-0">
                  <v-row>
                    <!-- First Name, Last Name  -->
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        :label="$t('registration.firstName')"
                        v-model="firstname"
                        :rules="firstnameRules"
                        required
                        outlined
                        dense
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        :label="$t('registration.lastName')"
                        v-model="lastname"
                        :rules="lastnameRules"
                        required
                        outlined
                        dense
                      >
                      </v-text-field>
                    </v-col>
                    <!-- Company Name -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-text-field
                        :label="$t('registration.companyName')"
                        v-model="companyName"
                        :rules="companyNameRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <!-- Company Website -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-text-field
                        :label="$t('registration.companyWebsite')"
                        v-model="companyWebsite"
                        :rules="companyWebsiteRules"
                        required
                        outlined
                        dense
                      >
                      </v-text-field>
                    </v-col>
                    <!-- Company Address -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-textarea
                        :label="$t('registration.companyAddress')"
                        v-model="companyAddress"
                        :rules="companyAddressRules"
                        :placeholder="$t('placeHolders.companyAddress')"
                        required
                        outlined
                        dense
                      ></v-textarea>
                    </v-col>
                    <!-- Company Phone -->
                    <v-col cols="12" md="12" class="py-0 mb-6" @mouseout="outOfTelInput">
                      <vue-tel-input
                        v-bind="bindProps"
                        :label="$t('registration.companyPhone')"
                        v-model="companyPhone"
                        outlined
                        dense
                        required
                        v-on:country-changed="countryChanged"
                        @open="onDropdownOpen(true)"
                        @close="onDropdownOpen(false)"
                        :rules="companyPhoneRules"
                        class="elevation-0"   
                      >
                        <template v-slot:arrow-icon>
                          <span>{{ open ? '▲' : '▼' }}</span>
                        </template>
                      </vue-tel-input>
                    </v-col>
                    <!-- Email -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-text-field
                        :label="$t('registration.email')"
                        type="email"
                        v-model="email"
                        :rules="emailRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <!-- Password, Confirm Password -->
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        :type="showPassword ? 'text' : 'password'"
                        :label="$t('registration.password')"
                        v-model="password"
                        :rules="passwordRules"
                        required
                        outlined
                        dense
                      >
                        <template v-slot:append>
                          <v-button @click="showPassword = !showPassword" tabindex="-1">
                            <v-icon v-if="showPassword">mdi-eye</v-icon>
                            <v-icon v-else-if="!showPassword">mdi-eye-off</v-icon>
                          </v-button>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-text-field
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :label="$t('registration.confirmPassword')"
                        v-model="confirmPassword"
                        :rules="confirmPasswordRules.concat(passwordConfirmationRule)"
                        @focusout="showPasswordDialog = true"
                        required
                        outlined
                        dense
                      >
                        <template v-slot:append>
                          <v-button @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
                            <v-icon v-if="showConfirmPassword">mdi-eye</v-icon>
                            <v-icon v-else-if="!showConfirmPassword">mdi-eye-off</v-icon>
                          </v-button>
                        </template>
                      </v-text-field>
                    </v-col>
                    <!-- Platform -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-autocomplete
                        ref="platform"
                        :label="$t('registration.platform')"
                        v-model="platform"
                        :items="platforms"
                        :rules="platformRules"
                        required
                        outlined
                        dense
                      ></v-autocomplete>
                    </v-col>
                    <!-- Account Number -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-text-field
                        ref = "accountNumber"
                        :label="accountNumberLabel"
                        v-model="accountNumber"
                        :rules="accountNumberRules"
                        required
                        outlined
                        dense
                        v-show="accountNumberFieldDisplay"
                      ></v-text-field>
                    </v-col>
                    <!-- Choose Region -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-autocomplete
                        ref="chooseRegion"
                        :label="$t('registration.chooseRegion')"
                        v-model="region"
                        :items="regions"
                        :rules="regionsRules"
                        outlined
                        dense
                      ></v-autocomplete>
                    </v-col>
                    <!-- CRM Instance -->
                    <v-col cols="12" md="12" class="py-0">
                      <v-autocomplete
                        :label="$t('registration.crmInstance')"
                        v-model="crmInstance"
                        :items="crms"
                        :rules="crmsRules"
                        outlined
                        dense
                       ></v-autocomplete>
                    </v-col> 
                  </v-row>
                  <!-- Agree Terms -->
                  <v-row>
                    <v-col cols="12" class="py-0">
                      <v-checkbox
                        @click.native="confirmDataDialog"
                        :label="$t('registration.agreeTerms')"
                        v-model="agreeToTerms"
                        :rules="agreeToTermsRules"
                        required
                        class="mt-0"
                        outlined
                        dense
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card-text>
            <v-card-actions class="pt-0 justify-center">
              <!-- <v-btn color="#D32F2F" dark class="mx-5 my-3" @click="resetForm" width="10em">Clear Form</v-btn>
              <v-spacer></v-spacer> -->
              <v-btn
                @click="submitSignUpForm"
                type="submit"
                color="#173862"
                class="ma-5 white--text"
                :disabled="signupButtonStatus"
                width="10em"
              >{{ $t('register') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <p class="mx-auto mt-3" :class="{ 'pb-5': $vuetify.breakpoint.xs }">
        {{ $t('registration.alreadyAccount') }} <router-link to="/login">{{ $t('login.login') }}</router-link>
      </p>

      <!-- <button @click="hideRecaptchaBadge">Hide recaptcha</button> -->
    </v-container>
  </div>
</template>

<script> 
  import { load } from 'recaptcha-v3';
  import i18n from '../i18n';
  import { customerMixin } from '../mixins/customerMixins';
  import { VueTelInput } from 'vue-tel-input';

  load(process.env.VUE_APP_RECAPTCHA_SITE_KEY,{
    useRecaptchaNet: true,
    explicitRenderParameters:{
      badge: 'bottomleft',
      tabindex: 3
    }
  });

  export default {
    components: {
      'eula-dialog': require('../components/Eula').default,
      'SignupPanel': require('@/components/LayoutSignupPanel').default,
      VueTelInput,
    },
    data: () => ({ 
      open: false,
      bindProps: {
        mode: 'auto',
        autoFormat: false,
        dropdownOptions: {
          showDialCodeInList: true,
          showDialCodeInSelection: true,
          showFlags: true,
        },
        required: true,
        validCharactersOnly: true
      },
      dialogEntitySubmit: false,
      showAlert: {},
      displayAlert: false,
      showPassword: false,
      showConfirmPassword: false,
      cancelSubmitConfirm: false,
      agreeToTerms: false,
      showPasswordDialog: false,
      agreeToTermsRules: [
        value =>
          !!value ||
          i18n.t('validation.agreeTerms.isRequired')
      ],
      awaitingValidation: false,
      regions: [
        {
          text: i18n.t('registration.region.na'),
          value: 'NA'
        },
        {
          text: i18n.t('registration.region.emea'),
          value: 'EMEA'
        },
        {
          text: i18n.t('registration.region.apac'),
          value: 'APAC'
        },
        {
          text: i18n.t('registration.region.latam'),
          value: 'LATAM'
        }
      ],
      regionsRules: [
        value => !!value || i18n.t('validation.region.isRequired')
      ],
      companyName: '',
      companyNameRules: [
        value => !!value || i18n.t('validation.companyName.isRequired')
      ],
      companyAddress: '',
      companyAddressRules: [
        value => !!value || i18n.t('validation.companyAddress.isRequired')
      ],
      companyPhone: '',
      companyPhoneRules: [
        value => !!value || i18n.t('validation.companyPhone.isRequired'),
      ],
      companyWebsite: '',
      companyWebsiteRules: [
        value => !!value || i18n.t('validation.companyWebsite.isRequired'),
        value => {
          if(value && value.trim() !== '') {
            const isURL = value.toLowerCase().match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
            if(isURL) {
              const url = value;
              let hostname = '';
              // Find hostname
              if (url.indexOf("//") > -1) hostname = url.split('/')[2];
              else hostname = url.split('/')[0];
              // Find & remove port number
              hostname = hostname.split(':')[0];
              // Find & remove "?"
              hostname = hostname.split('?')[0];
              // Converting hostname into lowercase
              hostname = hostname.toLowerCase();

              if(hostname.includes('www') && hostname.includes('.com')) return true;
              else return i18n.t('validation.companyWebsite.mustContain');

            } else {
              return i18n.t('validation.companyWebsite.properURL');
            }
          }
        }
      ],
      crms: [
        {
          text: 'Microsoft Dynamics',
          value: 'MSDYNAMICS'
        },
        {
          text: 'Salesforce',
          value: 'SALESFORCE'
        },
        {
          text: 'Zendesk',
          value: 'ZENDESK'
        },
      ],
      crmsRules: [
        value => {
          if(value && value.trim() !== '') {
            value.length >= 3 || i18n.t('validation.crm.isRequired')
          }
        }
      ],
      country: null,
      email: '',
      emailRules: [
        value => !!value || i18n.t('validation.email.isRequired'),
        value => /.+@.+\..+/.test(value) || i18n.t('validation.email.mustBeValid')
      ],
      firstname: '',
      firstnameRules: [
        value => !!value || i18n.t('validation.firstName.isRequired')
      ],
      formValidity: false,
      lastname: '',
      lastnameRules: [
        value => !!value || i18n.t('validation.lastName.isRequired')
      ],
      accountNumber: '',
      accountNumberFieldDisplay: false,
      accountNumberLabel: '',
      accountNumberRules: [],
      crmInstance: '',
      region: '',
      password: '',
      passwordRules: [
        value => !!value || i18n.t('validation.password.isRequired'),
        value => {
          const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
            return (
              pattern.test(value) ||
              i18n.t('validation.password.pattern')
            );
        }
      ],
      confirmPassword: '',
      confirmPasswordRules: [
        value => !!value || i18n.t('validation.password.isRequired'),
      ],
      platform: '',
      platforms: [
        {
          text: 'LivePerson',
          value: 'LIVEPERSON'
        },
        {
          text: 'Twilio Flex',
          value: 'TWILIOFLEX'
        },
      ],
      platformRules : [
        value => !!value || 'Platform is required.'
      ],
    }),
    methods: {
      outOfTelInput(){
        let el = document.getElementsByClassName('vue-tel-input')[0];
        el.style.borderColor = 'rgba(72, 71, 71, 0.6)';
        el.style.borderWidth = '1.5px';
      },
      onDropdownOpen(data){
        this.open = data;
      },
      countryChanged(country) {
        this.country = '+' + country.dialCode
      },
      resetForm() {
        this.email = ''
        this.$refs.signUpForm.reset()
      },
      async submitSignUpForm() {
        console.log(`[Signup] - submitSignUpForm - Let's Save New User Authorization!`)
        this.displayAlert = false;
        this.showAlert = {};
        this.dialogEntitySubmit = false;
        const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
        const token = await recaptcha.execute('login')

        let signUpData = {
          companyName: this.companyName,
          companyAddress: this.companyAddress,
          companyPhone: `${this.country}-${this.companyPhone.replace(/\s/g, "")}`,
          companyWebsite: this.companyWebsite,
          adminName: `${this.firstname} ${this.lastname}`,
          username: this.email,
          password: this.password,
          platform: this.platform,
          crmInstance: this.crmInstance,
          role: 'customer',
          recaptchaToken: token,
          accountRegion: this.region
        }

        if (this.platform === 'TWILIOFLEX') {
          signUpData.accountId = (Math.floor(Math.random()*90000) + 10000).toString();
          signUpData.accountSid = this.accountNumber;
        } else {
          signUpData.accountId =this.accountNumber;
        }

        if (this.password === this.confirmPassword) {
          this.$store
            .dispatch('users/signup', signUpData)
            .then((status) => {
              console.log(`[Signup] - submitSignUpForm - Added new user successfully.`)

              if (status) {
                // Dispaly alert
                this.displayAlert = true;
                this.showAlert = { 
                  type: 'success', 
                  message: i18n.t('registration.registrationSuccess'),
                  dismissable: true
                };

                this.resetForm();
                this.scrollToTop();
                // this.hideAlert();
              }
            })
            .catch(err => {
              console.error(`[Signup] - submitSignUpForm - Added new user Failure.`);
              this.error = err.response.data.error;

              // Dispaly alert
              this.displayAlert = true;
              this.showAlert = { 
                type: 'error', 
                message: (err.response.data.value.error.description) ? `${err.response.data.value.error.description}. ${i18n.t('registration.registrationError')}` : `${i18n.t('registration.registrationError')}`,
                dismissable: true
              };
              
              this.scrollToTop();
              // this.hideAlert();
            });
        } else {
          // Dispaly alert
          this.displayAlert = true;
          this.showAlert = { 
            type: 'error', 
            message: `${i18n.t('passwordClashing')}`,
            dismissable: true
          };
          
          this.scrollToTop();
        }
      },
      scrollToTop() {
        window.scrollTo(0,0);
      },
      hideAlert () {
        //Hide alert after 10 secs
        setTimeout(()=>{
          this.displayAlert = false;
          this.showAlert = {};
        }, 10000);
      },
      closePasswordDialog(){
        this.showPasswordDialog = false
        this.$refs.chooseRegion.focus();
      },
      confirmDataDialog() {
        // console.log(`confirmDataDialog...`);
        this.dialogEntitySubmit = true;
      }
      // delay: ms => new Promise(res => {
      //   setTimeout(res, ms)
      // }),
    },
    mixins: [customerMixin],
    computed: {
      passwordConfirmationRule(){
        return this.password === this.confirmPassword || i18n.t('validation.password.mustMatch');
      },
      signupButtonStatus() {
        if((this.firstname && this.firstname.trim() !== '')
          && (this.lastname && this.lastname.trim() !== '')
          && (this.companyName && this.companyName.trim() !== '') 
          && (this.companyWebsite && this.companyWebsite.trim() !== '')
          && (this.companyAddress && this.companyAddress.trim() !== '') 
          && (this.companyPhone && this.companyPhone.trim() !== '')
          && (this.email && this.email.trim() !== '')
          && (this.password && this.password.trim() !== '')
          && (this.confirmPassword && this.confirmPassword.trim() !== '') 
          && (this.platform && this.platform.trim() !== '') 
          && (this.region && this.region.trim() !== '')
          && (this.crmInstance && this.crmInstance.trim() !== '')
          && (this.agreeToTerms === true) 
          && (this.accountNumber && this.accountNumber.trim() !== '')
        ) {
          return false;
        } else {
          return true;
        }
      }
    },
    watch: {
      companyPhone: function(val) {
        let el = document.getElementsByClassName('vue-tel-input')[0];
        if(this.companyPhone === '' && val === '') {
          el.style.borderColor = '#ff5252';
          el.style.borderWidth = '2px';
        } else {
          el.style.borderColor = '#005caf';
          el.style.borderWidth = '2px';
        }
      },
      platform: function (val) {

        this.accountNumberFieldDisplay = true;
        this.accountNumber = '';
        this.accountNumberRules = [];
        // Based on platform modifying Account number text-field label and rules 
        if (val === 'TWILIOFLEX') {
          this.accountNumberLabel =  i18n.t('registration.accountSid')
          this.accountNumberRules = [
            value => !!value || i18n.t('validation.accountSid.isRequired'),
            value => {
              if (value.length === 2) return value.startsWith('AC', 0) || i18n.t('registration.invalidTwilioFlexAccountSid')
              if (value.length > 2 && value.length <= 34) {
                return (
                  (value.substring(0,2).startsWith('AC') && value.substring(2).match("^[0-9a-f]+$")) || i18n.t('registration.invalidTwilioFlexAccountSid')
                )
              }
            },
            value => {
              if (value.length < 34 || value.length > 34) return i18n.t('registration.invalidTwilioFlexAccountSid')
            }
          ]
        } else if (val === 'LIVEPERSON') {
          this.accountNumberLabel =  i18n.t('registration.accountNumber')
          this.accountNumberRules = [
             value => !!value || i18n.t('validation.accountNumber.isRequired')
          ]
        }
      },
      accountNumber: function () {
        if (this.platform === 'LIVEPERSON') {
          if (!this.awaitingValidation) {
            setTimeout(async () => {
              if (this.accountNumber.length) {
                const accountInfo = { 
                  accountId: this.accountNumber,
                  operation: 'signup', 
                  platform: this.platform
                }
                await this.verifyAccountID(accountInfo);

                this.accountNumberRules = [
                    value => !!value || i18n.t('validation.accountNumber.isRequired'),
                ]
                if (!this.accountIdValidationInfo.status) {
                  
                  this.accountNumberRules = [
                    value => !!value || i18n.t('validation.accountNumber.isRequired'),
                    value => this.accountIdValidationInfo.message
                  ]
                }             
              }
              this.awaitingValidation = false;
            }, 0); // 0 sec delay
          }
          this.awaitingValidation = true;
        }
      },
    },
  }
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"></style>
<style scoped>
.vue-tel-input {
  border-top-width: 1.5px;
  border-bottom-width: 1.5px;
  border-left-width: 1.5px;
  border-right-width: 1.5px;
  border-color: rgba(72, 71, 71, 0.6);
}
</style>